import { useUserStore } from '@/store/user.store'
import { showAlert } from '@/utils/alert/alertBridge'

let sessionExpiredHandled = false

const EXPIRED_CODE_KEYWORDS = ['EXPIRED', 'INVALID_TOKEN', 'UNAUTHORIZED', '401', '403']
const DUPLICATE_LOGIN_REASON = 'DUPLICATE_LOGIN'
const DUPLICATE_LOGIN_CODE_KEYWORDS = ['DUPLICATE_LOGIN', 'DUPLICATE_LOGIN_LOGOUT']

const readResultCode = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  return String(raw.resultCode ?? raw.code ?? raw?.data?.resultCode ?? raw?.data?.code ?? '').toUpperCase()
}

const readLogoutReason = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  return String(raw.logoutReason ?? raw.reason ?? raw?.data?.logoutReason ?? raw?.data?.reason ?? '').toUpperCase()
}

const readResultMessage = (raw) => {
  if (!raw || typeof raw !== 'object') return ''
  return String(raw.resultMessage ?? raw.message ?? raw?.data?.resultMessage ?? raw?.data?.message ?? '')
}

export const isDuplicateLoginInfo = (info) => {
  const raw = info?.raw
  const reason = readLogoutReason(raw)
  if (reason === DUPLICATE_LOGIN_REASON) return true

  const code = readResultCode(raw)
  return DUPLICATE_LOGIN_CODE_KEYWORDS.some((k) => code.includes(k))
}

export const isTokenExpiredInfo = (info) => {
  if (isDuplicateLoginInfo(info)) return false

  const remainSeconds = Number(info?.remainingSeconds)
  if (Number.isFinite(remainSeconds) && remainSeconds <= 0) return true

  const code = readResultCode(info?.raw)
  if (!code) return false
  return EXPIRED_CODE_KEYWORDS.some((k) => code.includes(k))
}

export const isUnauthorizedError = (error) => {
  const status = Number(error?.response?.status ?? error?.status ?? 0)
  return status === 401 || status === 403
}

export const getSessionEndMessage = (payload) => {
  const raw = payload?.raw ?? payload?.response?.data ?? payload?.data ?? payload

  const reason = readLogoutReason(raw)
  if (reason === DUPLICATE_LOGIN_REASON) {
    return '다른 로그인이 감지되어 로그아웃되었습니다. 다시 로그인해 주세요.'
  }

  const code = readResultCode(raw)
  if (DUPLICATE_LOGIN_CODE_KEYWORDS.some((k) => code.includes(k))) {
    return '다른 로그인이 감지되어 로그아웃되었습니다. 다시 로그인해 주세요.'
  }

  const serverMessage = readResultMessage(raw)
  if (serverMessage) {
    return serverMessage
  }

  return '로그인 시간이 만료되었습니다. 다시 로그인해 주세요.'
}

export const resetSessionExpiredHandled = () => {
  sessionExpiredHandled = false
}

export const handleSessionExpiredOnce = async (payloadOrRedirectTo = '/login', maybeRedirectTo) => {
  if (sessionExpiredHandled) return
  sessionExpiredHandled = true

  const redirectTo =
    typeof payloadOrRedirectTo === 'string'
      ? payloadOrRedirectTo
      : typeof maybeRedirectTo === 'string'
        ? maybeRedirectTo
        : '/login'

  // 토큰이 없는 경우(첫 방문/미로그인) → alert 없이 로그인으로 이동
  const hadToken = sessionStorage.getItem('accessToken')
  if (!hadToken) {
    sessionStorage.clear()
    window.location.href = redirectTo
    return
  }

  const message =
    typeof payloadOrRedirectTo === 'string'
      ? '로그인 시간이 만료되었습니다. 다시 로그인해 주세요.'
      : getSessionEndMessage(payloadOrRedirectTo)

  await showAlert(message)

  const redirect = window.location.pathname + window.location.search + window.location.hash
  try {
    const userStore = useUserStore()
    userStore.logout()
  } catch {
    // Ignore store initialization errors; fallback to storage clear below.
  }
  sessionStorage.clear()

  window.location.href = `${redirectTo}?redirect=${encodeURIComponent(redirect)}`
}
